/* eslint-disable no-nested-ternary */
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import coinbaseWallet from "../../assets/svg/coinbaseWallet.svg";
import walletConnect from "../../assets/svg/walletConnect.svg";
import importWallet from "../../assets/svg/import-wallet.svg";
import { useEIP6963Providers, EIP6963ProviderDetail } from "../../hooks/wallet/useEIP6963";
import {
  SDK_KIND_TO_WALLET_ENUM,
  WALLET_NAMES,
  WalletEnum,
} from "../../utils/wallet/types";
import { getWalletProviderKind } from "../../config/chains";
import { Divider } from "../shared/Divider";
import { WalletButton } from "./style";
import { shortenAddress } from "../../utils/strings";

export type IConnectWalletType = WalletEnum | "manualImport";

/** Extended type that carries EIP-6963 provider info for browser wallets */
export interface IWalletSelection {
  walletType: IConnectWalletType;
  eip6963Provider?: EIP6963ProviderDetail;
}

interface IConnectWalletProps {
  selectedWallet?: IConnectWalletType;
  connectedWallet?: IConnectWalletType;
  selectedEIP6963UUID?: string;
  setWallet: (wallet: IConnectWalletType, eip6963Provider?: EIP6963ProviderDetail) => void;
  isConnectingToWallet?: IConnectWalletType;
  importedWalletAddress?: string | null;
  hasBrowserWallet: boolean;
}

// Known rdns values to skip (already have dedicated buttons)
const SKIP_RDNS = new Set([
  "com.coinbase.wallet",      // Coinbase has its own button
]);

function ConnectWallet({
  selectedWallet,
  setWallet,
  isConnectingToWallet,
  connectedWallet,
  selectedEIP6963UUID,
  importedWalletAddress,
  hasBrowserWallet,
}: IConnectWalletProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "ConnectWalletModal.ConnectWallet",
  });

  const eip6963Providers = useEIP6963Providers();

  // Filter out providers that already have dedicated buttons
  const browserWallets = useMemo(
    () => eip6963Providers.filter((p) => !SKIP_RDNS.has(p.info.rdns)),
    [eip6963Providers]
  );

  const walletContent = useCallback(
    (wallet: WalletEnum, label?: string) => {
      if (isConnectingToWallet === wallet) {
        return t("open_your_wallet_to_connect");
      }

      if (connectedWallet === wallet) {
        return t("connected");
      }

      return label || WALLET_NAMES[wallet];
    },
    [connectedWallet, isConnectingToWallet, t]
  );

  // On SDK chains the native SDK modal is the picker; this list is not shown
  // (ConnectWalletModal hides the whole CONNECT_WALLET frame). Return nothing
  // so a missed hide cannot render a second "connecting" card.
  const sdkWalletEnum = SDK_KIND_TO_WALLET_ENUM[getWalletProviderKind()];
  if (sdkWalletEnum !== undefined) {
    return null;
  }

  return (
    <>
      {/* EIP-6963 discovered browser wallets (Rabby, MetaMask, Trust, OKX, Phantom, etc.) */}
      {browserWallets.map((detail) => {
        const isThisWallet = selectedEIP6963UUID === detail.info.uuid;
        const isThisConnected =
          connectedWallet === WalletEnum.EIP6963 && isThisWallet;
        return (
          <WalletButton
            key={detail.info.uuid}
            onClick={() => setWallet(WalletEnum.EIP6963, detail)}
            isActive={isThisWallet || isThisConnected}
            disabled={
              connectedWallet &&
              !(connectedWallet === WalletEnum.EIP6963 && isThisWallet)
            }
          >
            <img
              src={detail.info.icon}
              alt={detail.info.name}
              style={{ borderRadius: 6 }}
            />
            <span>
              {isConnectingToWallet === WalletEnum.EIP6963 && isThisWallet
                ? t("open_your_wallet_to_connect")
                : isThisConnected
                ? t("connected")
                : detail.info.name}
            </span>
          </WalletButton>
        );
      })}

      {/* Fallback: show generic "Browser Wallet" if no EIP-6963 providers but window.ethereum exists */}
      {browserWallets.length === 0 && hasBrowserWallet && (
        <WalletButton
          onClick={() => setWallet(WalletEnum.METAMASK)}
          isActive={
            selectedWallet === WalletEnum.METAMASK ||
            connectedWallet === WalletEnum.METAMASK
          }
          disabled={connectedWallet && connectedWallet !== WalletEnum.METAMASK}
        >
          <img
            src={importWallet}
            alt="browser wallet"
          />
          <span>{walletContent(WalletEnum.METAMASK, "Browser Wallet")}</span>
        </WalletButton>
      )}

      <WalletButton
        onClick={() => setWallet(WalletEnum.WALLETCONNECT)}
        isActive={
          selectedWallet === WalletEnum.WALLETCONNECT ||
          connectedWallet === WalletEnum.WALLETCONNECT
        }
        disabled={
          connectedWallet && connectedWallet !== WalletEnum.WALLETCONNECT
        }
      >
        <img src={walletConnect} alt="walletconnect" />
        <span>{walletContent(WalletEnum.WALLETCONNECT)}</span>
      </WalletButton>
      <WalletButton
        onClick={() => setWallet(WalletEnum.WALLETLINK)}
        isActive={
          selectedWallet === WalletEnum.WALLETLINK ||
          connectedWallet === WalletEnum.WALLETLINK
        }
        disabled={connectedWallet && connectedWallet !== WalletEnum.WALLETLINK}
      >
        <img src={coinbaseWallet} alt="coinbasewallet" />
        <span>{walletContent(WalletEnum.WALLETLINK)}</span>
      </WalletButton>
      <WalletButton
        onClick={() => setWallet("manualImport")}
        isActive={
          selectedWallet === "manualImport" ||
          connectedWallet === "manualImport"
        }
        disabled={connectedWallet && connectedWallet !== "manualImport"}
      >
        <img src={importWallet} alt="import" />
        <span>
          {importedWalletAddress
            ? `${shortenAddress(importedWalletAddress)} (Imported)`
            : t("manual_import")}
        </span>
      </WalletButton>
      <Divider direction="horizontal" size="100%" />
    </>
  );
}

export default ConnectWallet;
